import { powerProfileModes } from 'greener-ui-library';
import i18next from 'i18next';
import { PROJECT_TYPES } from 'utils/constants';
import { STRING_LENGTHS } from 'utils/constants';
import { validateTimeFormat } from 'utils/yupValidations';
import * as yup from 'yup';
import { BlueprintFormData } from './types';
import { RentalSource } from './utils/constants';
import { conventionalGensetFactor } from './utils/constants';
import { getPeakPowerFromCsv } from './utils/powerProfileCsvConverter';
import { DAYS_ORDER } from './utils/sortDays';

// Helper functions for validation
const createIntegerValidation = () => {
  return yup
    .mixed<number | string>()
    .required('validations.required')
    .test('is-number', 'validations.number', (value) => {
      if (!value) {
        return false;
      }
      // Replace comma with dot for European number format
      const normalizedValue = String(value).replace(',', '.');
      const num = Number(normalizedValue);
      return !isNaN(num);
    })
    .test('is-integer', 'validations.integer', (value) => {
      if (!value) {
        return false;
      }
      // Replace comma with dot for European number format
      const normalizedValue = String(value).replace(',', '.');
      const num = Number(normalizedValue);
      return Number.isInteger(num);
    })
    .test('is-positive', 'validations.mustBePositive', (value) => {
      if (!value) {
        return false;
      }
      // Replace comma with dot for European number format
      const normalizedValue = String(value).replace(',', '.');
      const num = Number(normalizedValue);
      return num >= 1;
    });
};

const createCostValidation = () => {
  return yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .required('validations.required')
    .test(
      'positive',
      () => i18next.t('validations.fieldMustBePositive'),
      (value) => value === undefined || value >= 0
    )
    .typeError('validations.number');
};

const createOptionalCostValidation = () => {
  return yup
    .number()
    .transform((value) => (isNaN(value) ? undefined : value))
    .optional()
    .test(
      'positive',
      () => i18next.t('validations.fieldMustBePositive'),
      (value) => value === undefined || value >= 0
    )
    .typeError('validations.number');
};

// Helper for conditional validation based on power profile mode
const createConditionalPowerValidation = (baseSchema: yup.Schema) => {
  return baseSchema.when('powerProfileMode', {
    is: powerProfileModes.manual,
    then: (schema) => schema.required('validations.required'),
    otherwise: (schema) => schema.optional(),
  });
};

// Helper for power number validation
const createPowerNumberValidation = () => {
  return yup
    .number()
    .nullable()
    .transform((value) => (isNaN(value) || value === null || value === '' ? undefined : value))
    .test(
      'non-negative',
      () => i18next.t('validations.fieldMustBePositive'),
      (value) => value === undefined || value === null || value >= 0
    )
    .typeError('validations.number');
};

export const createBlueprintSchema = (isEditMode: boolean = false) => {
  return yup.object().shape({
    // Step 1
    blueprintName: yup
      .string()
      .required('validations.required')
      .max(STRING_LENGTHS.MEDIUM, () => i18next.t('validations.tooLong', { max: STRING_LENGTHS.MEDIUM })),
    clientName: yup
      .string()
      .required('validations.required')
      .max(STRING_LENGTHS.MEDIUM, () => i18next.t('validations.tooLong', { max: STRING_LENGTHS.MEDIUM })),
    partnerOrganisation: yup.object().nullable().required('validations.required'),
    partnerOrganisationId: yup.number().nullable(),
    applicationType: yup
      .string()
      .oneOf([...Object.values(PROJECT_TYPES), ''], 'validations.notType')
      .default(''),
    startDate: yup.date().required('validations.required'),
    endDate: yup
      .date()
      .nullable()
      .transform((value) => {
        // Transform empty string, null, or invalid date to undefined
        if (value === '' || value === null || (value instanceof Date && isNaN(value.getTime()))) {
          return undefined;
        }
        return value;
      })
      .optional()
      .test('is-after-start', 'blueprints:wizard.validations.endDateAfterStartDate', function (value) {
        const startDate = this.parent.startDate;
        if (!value || !startDate) {
          return true; // Allow empty end date
        }
        // Normalize dates to midnight for accurate comparison
        const startDateNormalized = new Date(startDate);
        startDateNormalized.setHours(0, 0, 0, 0);
        const endDateNormalized = new Date(value);
        endDateNormalized.setHours(0, 0, 0, 0);

        return endDateNormalized >= startDateNormalized;
      }),

    // Step 2
    // Step 2 - Power Requirements
    powerProfileMode: yup
      .string()
      .oneOf([powerProfileModes.manual, powerProfileModes.file], 'validations.notType')
      .required('validations.required'),
    peakPowerKw: createConditionalPowerValidation(createPowerNumberValidation()),
    averagePowerKw: createConditionalPowerValidation(createPowerNumberValidation()),
    peaksPerDay: createConditionalPowerValidation(
      yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .test(
          'non-negative',
          () => i18next.t('validations.fieldMustBePositive'),
          (value) => value === undefined || value >= 0
        )
        .typeError('validations.number')
    ),
    peaksDuration: createConditionalPowerValidation(
      yup
        .number()
        .transform((value) => (isNaN(value) ? undefined : value))
        .test(
          'non-negative',
          () => i18next.t('validations.fieldMustBePositive'),
          (value) => value === undefined || value >= 0
        )
        .typeError('validations.number')
    ),
    restDayAveragePowerKw: createConditionalPowerValidation(createPowerNumberValidation()),
    workingDays: yup
      .array()
      .of(yup.string().oneOf(DAYS_ORDER, 'validations.notType').required())
      .when('powerProfileMode', {
        is: powerProfileModes.manual,
        then: (schema) => schema.required('validations.required').min(1, 'validations.required'),
        otherwise: (schema) => schema.optional(),
      })
      .defined(),
    workingHoursStart: yup.string().when('powerProfileMode', {
      is: powerProfileModes.manual,
      then: () => {
        if (isEditMode) {
          // In edit mode, make optional but keep format validation
          return yup
            .string()
            .optional()
            .test('is-valid-time', 'validations.time.invalidFormat', (value) => {
              if (!value) {
                return true; // Optional in edit mode
              }
              return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
            })
            .test('is-valid-hours', 'validations.time.invalidHours', (value) => {
              if (!value) {
                return true;
              }
              const [hours] = value.split(':');
              const hoursNum = parseInt(hours);
              return hoursNum >= 0 && hoursNum <= 23;
            })
            .test('is-valid-minutes', 'validations.time.invalidMinutes', (value) => {
              if (!value) {
                return true;
              }
              const [, minutes] = value.split(':');
              const minutesNum = parseInt(minutes);
              return minutesNum >= 0 && minutesNum <= 59;
            });
        } else {
          return validateTimeFormat();
        }
      },
      otherwise: () => yup.string().optional(),
    }),
    workingHoursEnd: yup.string().when('powerProfileMode', {
      is: powerProfileModes.manual,
      then: () => {
        if (isEditMode) {
          // In edit mode, make optional but keep format and time comparison validation
          return yup
            .string()
            .optional()
            .test('is-valid-time', 'validations.time.invalidFormat', (value) => {
              if (!value) {
                return true; // Optional in edit mode
              }
              return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
            })
            .test('is-valid-hours', 'validations.time.invalidHours', (value) => {
              if (!value) {
                return true;
              }
              const [hours] = value.split(':');
              const hoursNum = parseInt(hours);
              return hoursNum >= 0 && hoursNum <= 23;
            })
            .test('is-valid-minutes', 'validations.time.invalidMinutes', (value) => {
              if (!value) {
                return true;
              }
              const [, minutes] = value.split(':');
              const minutesNum = parseInt(minutes);
              return minutesNum >= 0 && minutesNum <= 59;
            })
            .test('is-after-start', 'validations.time.endBeforeStart', function (value) {
              const start = this.parent.workingHoursStart;
              if (!start || !value) {
                return true;
              }
              const [startHours, startMinutes] = start.split(':').map(Number);
              const [endHours, endMinutes] = value.split(':').map(Number);
              return endHours > startHours || (endHours === startHours && endMinutes > startMinutes);
            });
        } else {
          return validateTimeFormat().test('is-after-start', 'validations.time.endBeforeStart', function (value) {
            const start = this.parent.workingHoursStart;
            if (!start || !value) {
              return true;
            }
            const [startHours, startMinutes] = start.split(':').map(Number);
            const [endHours, endMinutes] = value.split(':').map(Number);
            return endHours > startHours || (endHours === startHours && endMinutes > startMinutes);
          });
        }
      },
      otherwise: () => yup.string().optional(),
    }),
    gensetKva: createIntegerValidation().test(
      'conventional-genset-power-requirement',
      'blueprints:wizard.validations.conventionalGensetUnderDimensionedError',
      function (value) {
        const gensetCount = this.parent.gensetCount;
        const powerProfileMode = this.parent.powerProfileMode;
        const peakPowerKw = this.parent.peakPowerKw;
        const editedPowerProfileCsv = this.parent.editedPowerProfileCsv;
        const rawPowerProfileCsv = this.parent.rawPowerProfileCsv;

        // Parse values
        const gensetKvaNum = typeof value === 'string' ? parseFloat(value) : value;
        const gensetCountNum = typeof gensetCount === 'string' ? parseFloat(gensetCount) : gensetCount;

        // If values are not valid numbers, skip this validation (other validations will catch it)
        if (!gensetKvaNum || isNaN(gensetKvaNum) || !gensetCountNum || isNaN(gensetCountNum)) {
          return true;
        }

        // Calculate max power peak
        let maxPowerPeak: number | undefined;

        if (powerProfileMode === powerProfileModes.file) {
          // File mode: get peak from CSV
          const csvData = editedPowerProfileCsv || rawPowerProfileCsv;
          if (csvData) {
            const peakFromCsv = getPeakPowerFromCsv(csvData);
            maxPowerPeak = peakFromCsv ? Math.ceil(peakFromCsv) : undefined;
          }
        } else if (powerProfileMode === powerProfileModes.manual) {
          // Manual mode: if chart was edited, use peak from edited CSV, otherwise use peakPowerKw
          const csvData = editedPowerProfileCsv || rawPowerProfileCsv;
          if (csvData) {
            const peakFromCsv = getPeakPowerFromCsv(csvData);
            maxPowerPeak = peakFromCsv ? Math.ceil(peakFromCsv) : undefined;
          } else {
            // Fallback to peakPowerKw if no CSV data
            maxPowerPeak = peakPowerKw ? Math.ceil(peakPowerKw) : undefined;
          }
        }

        // If we don't have maxPowerPeak yet, skip validation (will be validated when power profile is complete)
        if (!maxPowerPeak) {
          return true;
        }

        // Calculate total genset capacity and required capacity
        const totalGensetKva = gensetKvaNum * gensetCountNum;
        const requiredKva = conventionalGensetFactor * maxPowerPeak;

        // Check if under-dimensioned
        return totalGensetKva >= requiredKva;
      }
    ),
    gensetCount: createIntegerValidation().test(
      'conventional-genset-power-requirement',
      'blueprints:wizard.validations.conventionalGensetUnderDimensionedError',
      function (value) {
        const gensetKva = this.parent.gensetKva;
        const powerProfileMode = this.parent.powerProfileMode;
        const peakPowerKw = this.parent.peakPowerKw;
        const editedPowerProfileCsv = this.parent.editedPowerProfileCsv;
        const rawPowerProfileCsv = this.parent.rawPowerProfileCsv;

        // Parse values
        const gensetKvaNum = typeof gensetKva === 'string' ? parseFloat(gensetKva) : gensetKva;
        const gensetCountNum = typeof value === 'string' ? parseFloat(value) : value;

        // If values are not valid numbers, skip this validation (other validations will catch it)
        if (!gensetKvaNum || isNaN(gensetKvaNum) || !gensetCountNum || isNaN(gensetCountNum)) {
          return true;
        }

        // Calculate max power peak
        let maxPowerPeak: number | undefined;

        if (powerProfileMode === powerProfileModes.file) {
          // File mode: get peak from CSV
          const csvData = editedPowerProfileCsv || rawPowerProfileCsv;
          if (csvData) {
            const peakFromCsv = getPeakPowerFromCsv(csvData);
            maxPowerPeak = peakFromCsv ? Math.ceil(peakFromCsv) : undefined;
          }
        } else if (powerProfileMode === powerProfileModes.manual) {
          // Manual mode: if chart was edited, use peak from edited CSV, otherwise use peakPowerKw
          const csvData = editedPowerProfileCsv || rawPowerProfileCsv;
          if (csvData) {
            const peakFromCsv = getPeakPowerFromCsv(csvData);
            maxPowerPeak = peakFromCsv ? Math.ceil(peakFromCsv) : undefined;
          } else {
            // Fallback to peakPowerKw if no CSV data
            maxPowerPeak = peakPowerKw ? Math.ceil(peakPowerKw) : undefined;
          }
        }

        // If we don't have maxPowerPeak yet, skip validation (will be validated when power profile is complete)
        if (!maxPowerPeak) {
          return true;
        }

        // Calculate total genset capacity and required capacity
        const totalGensetKva = gensetKvaNum * gensetCountNum;
        const requiredKva = conventionalGensetFactor * maxPowerPeak;

        // Check if under-dimensioned
        return totalGensetKva >= requiredKva;
      }
    ),
    mainsConnection: yup.boolean().required('validations.required'),
    mainsConnectionCapacityUnit: yup
      .string()
      .when('mainsConnection', {
        is: true,
        then: (schema) => schema.required('validations.required'),
        otherwise: (schema) => schema.optional(),
      })
      .defined(),
    kitePowerScenario: yup.string().nullable().defined(),
    maxAmpsAvailable: yup
      .number()
      .when(['mainsConnection', 'mainsConnectionCapacityUnit'], {
        is: (mainsConnection, mainsConnectionCapacityUnit) =>
          mainsConnection === true && mainsConnectionCapacityUnit === 'amps',
        then: (schema) => schema.required('validations.required').min(0, 'validations.fieldMustBePositive'),
        otherwise: (schema) => schema.optional(),
      })
      .defined(),
    maxKwAvailable: yup
      .number()
      .when(['mainsConnection', 'mainsConnectionCapacityUnit'], {
        is: (mainsConnection, mainsConnectionCapacityUnit) =>
          mainsConnection === true && mainsConnectionCapacityUnit === 'kw',
        then: (schema) => schema.required('validations.required').min(0, 'validations.fieldMustBePositive'),
        otherwise: (schema) => schema.optional(),
      })
      .defined(),
    // Step 3 - Business Case Assumptions
    currency: yup.string().required('validations.required').oneOf(['EUR', 'GBP', 'USD'], 'validations.notType'),

    // Step 3 - Suggested Rentals for Hybrid Solution
    // Genset(s) section
    gensetRentalSource: yup
      .string()
      .oneOf([RentalSource.CATALOG, RentalSource.MANUAL, RentalSource.NONE], 'validations.notType')
      .required('validations.required'),
    gensetType: yup.string().required('validations.required').defined(),
    hybridGensetKva: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('gensetRentalSource', {
        is: RentalSource.MANUAL,
        then: (schema) => schema.required('validations.required').min(1, 'validations.mustBePositive'),
        otherwise: (schema) => schema.optional(),
      })
      .typeError('validations.number'),
    gensetRentalCount: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required('validations.required')
      .test(
        'positive',
        () => i18next.t('validations.mustBePositive'),
        (value) => value === undefined || value > 0
      )
      .typeError('validations.number'),
    gensetRentalCost: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('gensetRentalSource', {
        is: RentalSource.NONE,
        then: (schema) => schema.optional(),
        otherwise: (schema) =>
          schema.required('validations.required').test(
            'positive',
            () => i18next.t('validations.fieldMustBePositive'),
            (value) => value === undefined || value >= 0
          ),
      })
      .typeError('validations.number'),

    // Battery section
    batteryRentalSource: yup
      .string()
      .oneOf([RentalSource.CATALOG, RentalSource.MANUAL], 'validations.notType')
      .required('validations.required'),
    batteryModel: yup
      .string()
      .when('batteryRentalSource', {
        is: RentalSource.CATALOG,
        then: (schema) => schema.required('validations.required'),
        otherwise: (schema) => schema.optional(),
      })
      .defined(),
    batterySize: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('batteryRentalSource', {
        is: RentalSource.MANUAL,
        then: (schema) => schema.required('validations.required'),
        otherwise: (schema) => schema.optional(),
      })
      .test(
        'positive',
        () => i18next.t('validations.mustBePositive'),
        (value) => value === undefined || value > 0
      )
      .typeError('validations.number'),
    converterPower: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('batteryRentalSource', {
        is: RentalSource.MANUAL,
        then: (schema) => schema.required('validations.required'),
        otherwise: (schema) => schema.optional(),
      })
      .test(
        'positive',
        () => i18next.t('validations.mustBePositive'),
        (value) => value === undefined || value > 0
      )
      .typeError('validations.number'),
    batteryRentalCount: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .required('validations.required')
      .test(
        'positive',
        () => i18next.t('validations.mustBePositive'),
        (value) => value === undefined || value >= 1
      )
      .typeError('validations.number'),
    batteryRentalCost: createCostValidation(),

    // Price assumptions for hybrid setup with battery
    hybridDieselPricePerLiter: createCostValidation(),
    hybridElectricityPricePerKwh: yup
      .number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .when('mainsConnection', {
        is: true,
        then: (schema) => schema.required('validations.required'),
        otherwise: (schema) => schema.optional(),
      })
      .test(
        'positive',
        () => i18next.t('validations.fieldMustBePositive'),
        (value) => value === undefined || value >= 0
      )
      .typeError('validations.number'),
    hybridAdditionalCostsPerWeek: createOptionalCostValidation(),
    co2CostPerTon: createOptionalCostValidation(),

    // Price assumptions for conventional setup gensets
    conventionalDieselPricePerLiter: createCostValidation(),
    conventionalGensetRentalCostPerWeek: createCostValidation(),
    conventionalAdditionalCostsPerWeek: createOptionalCostValidation(),

    // Power Profile Data (CSV format)
    rawPowerProfileCsv: yup.string().when('powerProfileMode', {
      is: powerProfileModes.file,
      then: (schema) => schema.required('validations.required'),
      otherwise: (schema) => schema.optional(),
    }),
    editedPowerProfileCsv: yup.string().optional(),
    originalFileName: yup.string().optional(),
  }) satisfies yup.ObjectSchema<BlueprintFormData>;
};

// Update backward compatibility export
export const blueprintSchema = createBlueprintSchema(false, []);
